/**
 * Generate cool, unique names for analyses and implementations
 * Uses vocabulary words with numeric suffixes for uniqueness
 */

const adjectives = [
  // Cosmic & Space
  'stellar', 'quantum', 'cosmic', 'nova', 'nebula', 'galaxy', 'solar', 'lunar',
  'orbit', 'comet', 'asteroid', 'meteor', 'constellation', 'eclipse', 'aurora',
  
  // Energy & Power
  'atomic', 'plasma', 'fusion', 'laser', 'pulse', 'flux', 'spark', 'blaze',
  'ember', 'flare', 'radiant', 'luminous', 'electric', 'magnetic', 'kinetic',
  'dynamic', 'voltaic', 'thermal', 'nuclear', 'solar', 'wind', 'tidal',
  
  // Tech & Digital
  'neon', 'cyber', 'pixel', 'glitch', 'matrix', 'vector', 'synth', 'digital',
  'binary', 'crypto', 'neural', 'quantum', 'virtual', 'augmented', 'holographic',
  'algorithmic', 'computational', 'semantic', 'synthetic', 'robotic', 'automated',
  
  // Abstract & Cool
  'vortex', 'prism', 'zenith', 'phoenix', 'catalyst', 'nexus', 'echo', 'wave',
  'stream', 'crystal', 'diamond', 'pearl', 'jade', 'sapphire', 'ruby', 'emerald',
  'onyx', 'amber', 'ivory', 'silver', 'gold', 'platinum', 'titanium', 'chrome',
  
  // Nature & Elements
  'oceanic', 'volcanic', 'tectonic', 'meteorological', 'atmospheric', 'geological',
  'crystalline', 'mineral', 'organic', 'synthetic', 'elemental', 'primal',
  
  // Speed & Motion
  'velocity', 'momentum', 'acceleration', 'trajectory', 'propulsion', 'thrust',
  'turbo', 'supersonic', 'hypersonic', 'lightning', 'thunder', 'storm',
  
  // Mystical & Magical
  'arcane', 'mystic', 'ethereal', 'celestial', 'divine', 'transcendent',
  'enigmatic', 'cryptic', 'esoteric', 'occult', 'supernatural', 'paranormal'
];

const nouns = [
  // Analysis & Discovery
  'analysis', 'insight', 'breakthrough', 'discovery', 'exploration', 'investigation',
  'examination', 'study', 'research', 'inquiry', 'assessment', 'evaluation',
  'review', 'audit', 'survey', 'scan', 'probe', 'inspection', 'scrutiny',
  'appraisal', 'diagnosis', 'assessment', 'evaluation', 'review', 'audit',
  
  // Journey & Adventure
  'journey', 'mission', 'quest', 'venture', 'expedition', 'odyssey', 'voyage',
  'pursuit', 'crusade', 'campaign', 'endeavor', 'initiative', 'project',
  'pilgrimage', 'trek', 'safari', 'adventure', 'exploration', 'discovery',
  
  // Vision & Perspective
  'vision', 'perspective', 'outlook', 'viewpoint', 'lens', 'angle', 'approach',
  'strategy', 'blueprint', 'roadmap', 'framework', 'model', 'paradigm',
  
  // Implementation & Execution
  'implementation', 'execution', 'deployment', 'launch', 'rollout', 'release',
  'delivery', 'deliverable', 'deployment', 'release', 'launch', 'rollout',
  'delivery', 'shipment', 'dispatch', 'transmission', 'propagation', 'dissemination',
  
  // Creation & Building
  'creation', 'construction', 'assembly', 'fabrication', 'manufacturing', 'production',
  'development', 'engineering', 'architecting', 'designing', 'building', 'crafting',
  'sculpting', 'forging', 'molding', 'shaping', 'forming', 'generating',
  
  // Transformation & Change
  'transformation', 'evolution', 'revolution', 'metamorphosis', 'transition',
  'migration', 'conversion', 'adaptation', 'modification', 'enhancement',
  'optimization', 'refinement', 'improvement', 'upgrade', 'renovation',
  
  // Achievement & Success
  'achievement', 'accomplishment', 'milestone', 'landmark', 'benchmark',
  'victory', 'triumph', 'success', 'breakthrough', 'advancement', 'progress',
  
  // System & Structure
  'system', 'architecture', 'infrastructure', 'framework', 'platform', 'foundation',
  'structure', 'scaffold', 'skeleton', 'backbone', 'core', 'essence',
  
  // Code & Tech
  'codebase', 'repository', 'module', 'component', 'service', 'application',
  'platform', 'system', 'engine', 'framework', 'library', 'toolkit'
];

/**
 * Generate an alphanumeric suffix (letters + numbers)
 * Examples: "A42", "X7K", "B9M", "Z12"
 */
function generateAlphanumericSuffix() {
  const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // Exclude I, O to avoid confusion
  const numbers = '0123456789';
  
  // Random pattern: Letter + 1-2 digits + optional letter
  const pattern = Math.random() < 0.5 ? 'LNN' : 'LNNL'; // L=Letter, N=Number
  
  let suffix = '';
  for (const char of pattern) {
    if (char === 'L') {
      suffix += letters[Math.floor(Math.random() * letters.length)];
    } else {
      suffix += numbers[Math.floor(Math.random() * numbers.length)];
    }
  }
  
  return suffix;
}

/**
 * Generate a unique name for an execution (INTERNAL - lowercase with dashes)
 * Format: {adjective}-{noun}-{suffix}
 * Example: "stellar-analysis-A42"
 * 
 * NOTE: This is only used internally by generateDisplayName.
 * Use generateDisplayName() for user-facing names.
 */
export function generateExecutionName(type = 'analysis', existingNames = []) {
  const typeNoun = type === 'analysis' 
    ? nouns.filter(n => !n.includes('implementation') && !n.includes('execution') && !n.includes('deployment'))
    : nouns.filter(n => n.includes('implementation') || n.includes('execution') || n.includes('deployment'));
  
  const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = typeNoun[Math.floor(Math.random() * typeNoun.length)];
  
  // Generate alphanumeric suffix to ensure uniqueness
  let suffix = generateAlphanumericSuffix();
  let name = `${adjective}-${noun}-${suffix}`;
  
  // Ensure uniqueness by regenerating suffix if needed
  let attempts = 0;
  while (existingNames.includes(name) && attempts < 100) {
    suffix = generateAlphanumericSuffix();
    name = `${adjective}-${noun}-${suffix}`;
    attempts++;
  }
  
  return name;
}

/**
 * Generate a display name for user-facing display
 * Format: "{adjective}-{noun}-{suffix}" (lowercase, dashes, alphanumeric suffix)
 * Example: "stellar-analysis-a42", "quantum-implementation-x7k"
 * 
 * This is the function to use in modals and UI components.
 */
export function generateDisplayName(type = 'analysis', existingNames = []) {
  return generateExecutionName(type, existingNames);
}
